import { RolesWithPermissions, Permissions, User } from '../types';
import { SALFA_RAC_EXT_BUSINESS_UNIT_ID } from './constants';

const hasBusinessUnitAccess = (user: User, targetUser: Omit<User, 'id'> & { id?: number }): boolean => {
  return !!user.businessUnitIds?.some((businessUnitId) => targetUser.businessUnitIds?.includes(businessUnitId));
};

const ROLES = {
  admin: {
    clients: {
      viewAndUpdate: true,
      assign: true,
    },
    users: {
      view: true,
      update: true,
      create: true,
      delete: true,
    },
  },
  businessUnitAdmin: {
    clients: {
      viewAndUpdate: (user, client) => user.businessUnitIds?.includes(client.businessUnitId) || false,
      assign: (user, client) => (client && user.businessUnitIds?.includes(client.businessUnitId)) || false,
    },
    users: {
      view: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.role !== 'admin' &&
        targetUser.isAdmin !== true,
      update: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.role !== 'admin' &&
        targetUser.isAdmin !== true,
      create: (user, targetUser) => {
        if (!targetUser) return true; // This means that the user is being created and doesn't have businessUnitIds yet
        return hasBusinessUnitAccess(user, targetUser) && targetUser.role !== 'admin' && targetUser.isAdmin !== true;
      },
      delete: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.role !== 'admin' &&
        targetUser.isAdmin !== true,
    },
  },
  branchManager: {
    clients: {
      viewAndUpdate: (user, client) =>
        (user.businessUnitIds?.includes(client.businessUnitId) || false) &&
        !client.isTestClient &&
        (client.chatType === 'whatsapp' || client.userId === user.id) &&
        (user.brandsToSell && user.brandsToSell.length > 0
          ? user.brandsToSell.includes(client.brandOfInterest || '')
          : true),
      assign: (user, client) =>
        (client &&
          user.businessUnitIds?.includes(client.businessUnitId) &&
          (user.brandsToSell && user.brandsToSell.length > 0
            ? user.brandsToSell.includes(client.brandOfInterest || '')
            : true)) ||
        false,
    },
    users: {
      view: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.isAdmin !== true &&
        (targetUser.role === 'seller' || targetUser.role === 'sellerWithAssignment') &&
        (user.brandsToSell ? user.brandsToSell.some((brand) => targetUser.brandsToSell?.includes(brand)) : true),
      update: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.isAdmin !== true &&
        (targetUser.role === 'seller' || targetUser.role === 'sellerWithAssignment') &&
        (user.brandsToSell && user.brandsToSell.length > 0
          ? user.brandsToSell.some((brand) => targetUser.brandsToSell?.includes(brand))
          : true),
      create: (user, targetUser) => {
        if (!targetUser) return true; // This means that the user is being created and doesn't have businessUnitIds yet
        return (
          !!targetUser &&
          hasBusinessUnitAccess(user, targetUser) &&
          targetUser.isAdmin !== true &&
          (targetUser.role === 'seller' || targetUser.role === 'sellerWithAssignment') &&
          (user.brandsToSell && user.brandsToSell.length > 0
            ? user.brandsToSell.some((brand) => targetUser.brandsToSell?.includes(brand))
            : true)
        );
      },
      delete: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.isAdmin !== true &&
        (targetUser.role === 'seller' || targetUser.role === 'sellerWithAssignment') &&
        (user.brandsToSell && user.brandsToSell.length > 0
          ? user.brandsToSell.some((brand) => targetUser.brandsToSell?.includes(brand))
          : true),
    },
  },
  seller: {
    clients: {
      viewAndUpdate: (user, client) =>
        (user.businessUnitIds?.includes(client.businessUnitId) || false) &&
        !client.isTestClient &&
        (client.userId === user.id || client.businessUnitId === SALFA_RAC_EXT_BUSINESS_UNIT_ID),
      assign: false,
    },
    users: {
      view: (user, targetUser) => !!targetUser && targetUser.id === user.id,
      update: (user, targetUser) => !!targetUser && targetUser.id === user.id,
    },
  },
  sellerWithAssignment: {
    clients: {
      viewAndUpdate: (user, client) =>
        (user.businessUnitIds?.includes(client.businessUnitId) || false) &&
        !client.isTestClient &&
        (client.userId === user.id || client.businessUnitId === SALFA_RAC_EXT_BUSINESS_UNIT_ID),
      assign: (user, client) => (client && user.businessUnitIds?.includes(client.businessUnitId)) || false,
    },
    users: {
      view: (user, targetUser) => !!targetUser && targetUser.id === user.id,
      update: (user, targetUser) => !!targetUser && targetUser.id === user.id,
    },
  },
  originAdmin: {
    clients: {
      viewAndUpdate: false,
      assign: false,
    },
    users: {
      view: (user, targetUser) =>
        !!targetUser &&
        hasBusinessUnitAccess(user, targetUser) &&
        targetUser.isAdmin !== true &&
        user.originId === targetUser.originId,
    },
  },
} as const satisfies RolesWithPermissions;

// eslint-disable-next-line import/prefer-default-export
export function hasPermission<Resource extends keyof Permissions>(
  user: User,
  resource: Resource,
  action: Permissions[Resource]['action'],
  data?: Permissions[Resource]['dataType']
): boolean {
  const permission = (ROLES as RolesWithPermissions)[user.role][resource]?.[action];
  if (permission === null || permission === undefined) return false;
  if (typeof permission === 'boolean') return permission;
  if (data === undefined) return false;
  return permission(user, data);
}
